import { Entity } from '@api';
import { RangeHorizon } from '@chart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { TeamsGuard } from '@terminal/config/TeamsGuard';
import { useCompanyCompetitors } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '@terminal/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import {
  getChartSubPageSearchParams,
  SnakeTypes,
} from '@terminal/widgets/chart-peer-group/utils/chart-subpage-navigation/chart-subpage-navigation';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { MultiLineChartModule } from '../multi-line-chart/MultiLineChartModule';
import styles from './OverviewFinancials.module.scss';

export interface OverviewFinancialsProps {
  entity: Entity;
  isDrawer?: boolean;
}

const COMPETITORS_AMOUNT = 4;

export const OverviewFinancials = ({
  entity,
  isDrawer = false,
}: OverviewFinancialsProps) => {
  const { t } = useTranslation('asset-overview');
  const [searchParams] = useSearchParams();

  const { competitorsData, isFetching, isError, error } = useCompanyCompetitors(
    {
      entityTag: entity.tag,
      limit: COMPETITORS_AMOUNT,
    }
  );

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '350px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError) {
    if (error?.status === 404) {
      return null;
    }

    return <ErrorMessageWrapper displayIcon />;
  }

  const competitorEntities = competitorsData.map(({ entity }) => entity);
  const entities = [entity, ...competitorEntities];

  const chartSubPageSearchParams = getChartSubPageSearchParams({
    entityTag: entity.tag,
    prevSearchParams: searchParams,
    horizon: RangeHorizon.OneYear,
    snakeType: SnakeTypes.Price,
  }).toString();

  const linkToChartSubPage = `${appPaths.assetOverview}?${chartSubPageSearchParams}`;

  return (
    <div className={clsx(styles.container, { [styles.isDrawer]: isDrawer })}>
      <TeamsGuard>
        <SectionTitle
          title={t('asset-overview:performance')}
          linkTo={linkToChartSubPage}
        />
      </TeamsGuard>

      <p className={styles.title}>
        {t('asset-overview:last12Months')}
        <span>%</span>
      </p>

      <MultiLineChartModule
        entities={entities}
        chartConfig={{
          enableZoom: false,
        }}
        tooltipSortConfig={{ field: 'priceFormatted', desc: true }}
        source={
          isDrawer
            ? POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW_DRAWER
            : POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW
        }
      />
    </div>
  );
};
